import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { PilotId } from '~/eds/src/types/PilotId';
import { scrollElementIntoView } from '~/eds/src/utils/scrollElementIntoView';
import {
  ClauseContent,
  DocumentViewClauses as DocumentViewClause,
} from '~/types/Clause';

import { Clause } from './Clause';

interface Props {
  activeClauseId?: PilotId;
  clauses: DocumentViewClause[];
  search?: string;
  onToggle?: (isExpanded: boolean, clause?: DocumentViewClause) => void;
  /** if true, all accordion items will be expanded */
  isAllExpanded?: boolean;
  /** when provided, the clause will be expanded and will the screen will scroll to the highlight. */
  featuredClauses?: string[];

  onClauseClick?: (clause: ClauseContent) => void;
}

export function ClausesAccordion({
  activeClauseId,
  clauses,
  featuredClauses,
  isAllExpanded = false,
  search = '',
  onClauseClick,
  onToggle,
}: Props) {
  const intl = useIntl();

  const getClauseContent = (clauses: ClauseContent[]) => (
    <Clause
      clauses={clauses}
      onClick={onClauseClick}
      activeClauseId={activeClauseId}
    />
  );

  const clausesAccordionItems = useMemo(() => {
    return clauses.map((clause) => ({
      content: getClauseContent(clause.content),
      data: clause,
      title: `${clause.name} (${clause.content.length})`,
      isExpanded: isAllExpanded || featuredClauses?.includes(clause.name),
    }));
  }, [clauses, activeClauseId]);

  useEffect(() => {
    if (featuredClauses) {
      const clause = clauses.find((clause) =>
        featuredClauses?.includes(clause.name),
      );
      const title = `${clause?.name} (${clause?.content.length})-header`;
      scrollElementIntoView(title, { block: 'center' });
    }
  }, [featuredClauses, clauses]);

  const placeholderContent = !clauses.length
    ? {
        icon: 'field-clause' as const,
        message: intl.formatMessage({
          id: 'ClausesAccordion.noClausesMessage',
          defaultMessage: 'No clauses exists for this document.',
          description: 'Message shown when no clauses exist for a document',
        }),
      }
    : undefined;

  return (
    <ContentContainer placeholderContent={placeholderContent}>
      <Accordion<DocumentViewClause>
        items={clausesAccordionItems}
        search={search}
        onToggle={(isExpanded, item) => onToggle?.(isExpanded, item.data)}
      />
    </ContentContainer>
  );
}
